import {
  isBrowserExtensionRuntimeIssue,
  isUnattributableInjectedRejection,
} from '../utils/browserExtensionErrorGuard';
import { isStaleBuildError, recoverFromStaleBuild } from '../utils/staleBuildRecovery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const DEVICE_ID_KEY = 'lingoDeviceId';
const RECENT_WINDOW_MS = 30000;
// A dropped connection repeats for as long as the connection is bad, and every
// report has to travel over that same connection. Hold repeats of one transport
// failure for much longer than an application error: polling screens used to
// post one report per poll for hours.
const TRANSPORT_WINDOW_MS = 10 * 60 * 1000;
const recentReports = new Map();

const createDeviceId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = createDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const cleanRecentReports = () => {
  const now = Date.now();
  recentReports.forEach((time, key) => {
    if (now - time > TRANSPORT_WINDOW_MS) recentReports.delete(key);
  });
};

const shouldSkip = (key, windowMs = RECENT_WINDOW_MS) => {
  cleanRecentReports();
  const now = Date.now();
  const last = recentReports.get(key);
  if (last && now - last < windowMs) return true;
  recentReports.set(key, now);
  return false;
};

// The failures dashboard is for production. A report from a dev server describes
// an editor state, and the backend drops those anyway — skip the POST too.
const isDevHost = () => {
  if (typeof window === 'undefined') return false;
  return /^(localhost|127\.0\.0\.1|\[::1\]|10\.0\.2\.2|192\.168\.)/i.test(window.location.hostname);
};

// A request that never got a response: the user's connection, not a defect.
const isTransportFailure = (statusCode, message) => (
  !statusCode
  && /Network Error|timeout of \d+ms exceeded|Request aborted|ERR_NETWORK|ECONNABORTED|Failed to fetch|Load failed/i.test(
    String(message || ''),
  )
);

const currentRoute = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

const viewport = () => {
  if (typeof window === 'undefined') return '';
  return `${window.innerWidth}x${window.innerHeight}`;
};

const getSession = () => ({
  isGuest: localStorage.getItem('guestMode') === 'true',
  nativeLanguage: localStorage.getItem('nativeLanguage') || '',
  targetLanguage: localStorage.getItem('targetLanguage') || '',
  subscriptionTier: localStorage.getItem('subscriptionTier') || '',
});

const getClient = () => ({
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  language: navigator.language,
  viewport: viewport(),
});

const sendReport = (payload) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'X-Lingo-Device-Id': getDeviceId(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const body = JSON.stringify({
    source: 'web',
    route: currentRoute(),
    userId: localStorage.getItem('userId') || '',
    username: localStorage.getItem('username') || '',
    role: localStorage.getItem('userRole') || '',
    subscriptionTier: localStorage.getItem('subscriptionTier') || '',
    deviceId: getDeviceId(),
    session: getSession(),
    client: getClient(),
    ...payload,
  });

  fetch(`${API_URL}/error-reports`, {
    method: 'POST',
    headers,
    body,
    keepalive: body.length < 60000,
  }).catch(() => {});
};

export const reportClientError = (payload = {}) => {
  if (isDevHost()) return;

  const message = String(payload.message || payload.error?.message || 'Client failure reported');
  const key = [
    payload.kind || 'manual',
    payload.severity || 'error',
    payload.route || currentRoute(),
    payload.api?.method || '',
    payload.api?.url || '',
    payload.api?.statusCode || '',
    message,
  ].join('|');

  const dedupeWindowMs = isTransportFailure(payload.api?.statusCode, message)
    ? TRANSPORT_WINDOW_MS
    : RECENT_WINDOW_MS;
  if (shouldSkip(key, dedupeWindowMs)) return;

  sendReport({
    kind: payload.kind || 'manual',
    severity: payload.severity || 'error',
    // Stable operation code + correlation ref from the backend response, so the
    // client-side record lines up with the server-side one in the dashboard.
    code: payload.code || undefined,
    ref: payload.ref || undefined,
    message,
    stack: payload.stack || payload.error?.stack || '',
    componentStack: payload.componentStack || '',
    screen: payload.screen || '',
    api: payload.api || undefined,
    metadata: payload.metadata || {},
  });
};

// Expected, non-defect outcomes we never surface on the failures dashboard.
// Mirrors backend/utils/errorReportPolicy.js EXPECTED_CODES — the backend drops
// these too (so older installed builds are covered), and skipping the POST here
// saves a request on connections that are often already struggling.
//
// Three families: session lifecycle (a stale token fans out to a 401 on every
// in-flight request), product rules doing their job (wrong password, email
// already verified, study language must differ from native), and control-flow
// responses a caller is written to handle (no script onboarding for this target,
// nothing pronounceable in a string). Genuine token problems
// (AUTH_TOKEN_INVALID) and admin-route refusals still report.
const SUPPRESSED_REPORT_CODES = new Set([
  'AUTH_TOKEN_EXPIRED', 'SESSION_EXPIRED', 'AUTH_NO_TOKEN', 'AUTH_SESSION_REVOKED', 'DB_UNAVAILABLE',
  'AUTH_LOGIN_INVALID_CREDENTIALS', 'AUTH_EMAIL_ALREADY_EXISTS',
  'AUTH_VERIFY_EMAIL_INVALID_TOKEN', 'AUTH_RESEND_VERIFICATION_ALREADY_VERIFIED',
  'AUTH_RESEND_VERIFICATION_RATE_LIMITED', 'AUTH_REFRESH_TOKEN_INVALID',
  'COHORT_LANGUAGE_SAME_AS_NATIVE', 'COHORT_SCHOLARSHIP_STATEMENT_REQUIRED',
  'COHORT_FREE_LIMIT', 'COHORT_ACTIVITY_ACCESS_UNAVAILABLE',
  'PRACTICE_CONTEXT_ACCESS_FORBIDDEN', 'CURRICULUM_V2_NOT_ENABLED', 'PROFICIENCY_TEST_PAYMENT_REQUIRED',
  'ADMIN_COHORT_SCHOLARSHIP_CLOSE_REQUIRED', 'ADMIN_SEND_EMAIL_NO_RECIPIENTS',
  'CURRICULUM_V2_ALPHABET_UNAVAILABLE', 'LESSONS_BOOTSTRAP_LANGUAGE_PAIR_MISMATCH',
  'TTS_TEXT_NOT_SPEAKABLE',
]);

export const reportApiError = (error, metadata = {}) => {
  if (!error || error.__reportedToAdmin) return;
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return;

  const config = error.config || {};
  const url = config.url || '';
  if (url.includes('/error-reports')) return;

  if (SUPPRESSED_REPORT_CODES.has(error.response?.data?.code)) {
    error.__reportedToAdmin = true;
    return;
  }

  // Fire-and-forget telemetry (the session heartbeat, lesson event beacons)
  // marks itself `background`. Nobody waits on it and the next beat carries
  // the same data, so losing one to a flaky connection is not worth a
  // dashboard row: these were 150+ of the open reports in 2026-10. A response
  // the server actually sent (any status) still reports.
  if (config.background && isTransportFailure(error.response?.status, error.message)) {
    error.__reportedToAdmin = true;
    return;
  }

  error.__reportedToAdmin = true;
  const statusCode = error.response?.status;
  const responseMessage = error.response?.data?.message || error.response?.data?.error || error.message;
  // Backend now returns a stable operation code + correlation ref on failures.
  const serverCode = error.response?.data?.code;
  const serverRef = error.response?.data?.ref;
  // A recognised transport failure is the connection, not the app, so it is
  // information rather than a fault. Everything else keeps the severity it had
  // before: 5xx and any other response-less failure are errors, 4xx is a
  // warning. (Downgrading unrecognised response-less failures would hide the
  // case where our own request never left the app.)
  const severity = isTransportFailure(statusCode, responseMessage || error.message)
    ? 'info'
    : ((statusCode >= 500 || !statusCode) ? 'error' : 'warning');

  reportClientError({
    kind: 'api',
    severity,
    code: serverCode,
    ref: serverRef,
    message: responseMessage || 'API request failed',
    stack: error.stack || '',
    api: {
      method: String(config.method || 'get').toUpperCase(),
      url,
      statusCode,
      statusText: error.response?.statusText || '',
      responseMessage,
    },
    metadata: {
      serverCode: serverCode || '',
      serverRef: serverRef || '',
      axiosCode: error.code || '',
      timeout: config.timeout || '',
      ...metadata,
    },
  });
};

export const installGlobalErrorReporting = () => {
  if (window.__lingoErrorReportingInstalled) return;
  window.__lingoErrorReportingInstalled = true;

  window.addEventListener('error', (event) => {
    if (isBrowserExtensionRuntimeIssue({
      message: event.message,
      filename: event.filename,
      stack: event.error?.stack,
      reason: event.error,
    })) return;

    // "Script error." with no file, line or stack is the browser masking an
    // error thrown by a cross-origin script: in practice code a mobile browser
    // injects into every page (the 2026-09 reports all came from Phoenix).
    // There is nothing in it to act on. Our own bundle is same-origin, so its
    // errors always carry a filename and are unaffected.
    if (/^Script error\.?$/i.test(String(event.message || '').trim()) && !event.filename && !event.error?.stack) return;

    // A chunk that no longer exists means this tab is running a build that has
    // been replaced. Reload once to pick up the current one, and report it so a
    // build that is genuinely broken still shows up.
    if (isStaleBuildError({
      message: event.message,
      name: event.error?.name,
      filename: event.filename,
    })) {
      reportClientError({
        kind: 'runtime',
        severity: 'warning',
        message: event.message || 'Stale build asset',
        stack: event.error?.stack || '',
        metadata: { filename: event.filename || '', staleBuild: true },
      });
      recoverFromStaleBuild();
      return;
    }

    reportClientError({
      kind: 'runtime',
      severity: 'error',
      message: event.message || event.error?.message || 'Runtime error',
      stack: event.error?.stack || '',
      metadata: {
        filename: event.filename || '',
        lineno: event.lineno || '',
        colno: event.colno || '',
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason || {};
    if (isBrowserExtensionRuntimeIssue({
      message: reason.message || String(reason || 'Unhandled promise rejection'),
      stack: reason.stack || '',
      reason,
    })) return;

    // Injected page script: no frame in any of our assets and nothing in the
    // message to act on.
    if (isUnattributableInjectedRejection({
      message: reason.message || String(reason || ''),
      stack: reason.stack || '',
    })) return;

    // A lazy route import rejects rather than throwing, so the stale-build check
    // belongs here too.
    if (isStaleBuildError({ message: reason.message, name: reason.name })) {
      reportClientError({
        kind: 'unhandled-rejection',
        severity: 'warning',
        message: reason.message || 'Stale build asset',
        stack: reason.stack || '',
        metadata: { staleBuild: true },
      });
      recoverFromStaleBuild();
      return;
    }

    reportClientError({
      kind: 'unhandled-rejection',
      severity: 'error',
      message: reason.message || String(reason || 'Unhandled promise rejection'),
      stack: reason.stack || '',
      metadata: {
        name: reason.name || '',
        code: reason.code || '',
      },
    });
  });
};
